<template>
  <div class="page">
    <div class="bg-grid" />
    <TopBar />
    <DashboardNav />

    <main class="content">
      <!-- Stat cards -->
      <div class="stat-row">
        <StatCard label="TOTAL SPEND" :value="`$${stats.total_cost.toFixed(2)}`" sub="all time" />
        <StatCard label="THIS MONTH" :value="`$${stats.month_cost.toFixed(2)}`" sub="month to date" trend="neutral" />
        <StatCard label="TOTAL REQUESTS" :value="stats.total_messages.toLocaleString()" sub="messages processed" />
        <StatCard label="COST / REQUEST" :value="`$${stats.cost_per_req.toFixed(4)}`" sub="average" :trend="costTrend" />
      </div>

      <div class="main-grid">
        <!-- Spend Breakdown -->
        <div class="spend-col">
          <SectionHeader label="Spend by Project" :meta="`${per_project.length} projects`" />
          <div class="spend-list">
            <div v-if="per_project.length === 0" class="empty-row">No spend data yet.</div>
            <div v-for="p in per_project" :key="p.uuid" class="spend-row">
              <div class="spend-info">
                <span class="spend-name">{{ p.name }}</span>
                <span class="spend-msgs">{{ p.messages.toLocaleString() }} msgs</span>
              </div>
              <ProgressBar :value="p.cost" :max="maxCost" color="amber" />
              <span class="spend-amount">${{ p.cost.toFixed(4) }}</span>
            </div>
          </div>
        </div>

        <!-- Right column -->
        <div class="right-col">
          <!-- Daily Cost Chart -->
          <div class="chart-panel">
            <div class="panel-head">
              <span class="panel-label">DAILY COST</span>
              <span class="panel-meta">last 30 days</span>
            </div>
            <div class="chart-body">
              <BarChart
                :data="chartData"
                color="#f5a623"
              />
            </div>
          </div>

          <!-- Project ROI Table -->
          <div class="roi-panel">
            <div class="panel-head">
              <span class="panel-label">PROJECT ROI</span>
            </div>
            <div class="roi-table">
              <div class="roi-header">
                <span class="roi-col-name">PROJECT</span>
                <span class="roi-col-num">REQUESTS</span>
                <span class="roi-col-num">COST</span>
                <span class="roi-col-num">SCORE</span>
              </div>
              <div v-for="p in per_project" :key="p.uuid" class="roi-row">
                <span class="roi-col-name roi-name">{{ p.name }}</span>
                <span class="roi-col-num roi-num">{{ p.messages.toLocaleString() }}</span>
                <span class="roi-col-num roi-num">${{ p.cost.toFixed(4) }}</span>
                <span class="roi-col-num roi-score" :style="{ color: scoreColor(p.score) }">
                  {{ p.score > 0 ? p.score.toFixed(1) + "%" : "—" }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Optimizations Log -->
      <div v-if="improvements.length > 0" class="opt-section">
        <SectionHeader label="Optimizations Log" :meta="`${improvements.length} improvements applied`" />
        <div class="opt-grid">
          <div v-for="r in improvements" :key="r.id" class="opt-card">
            <span class="opt-project">{{ r.project_name }}</span>
            <BeforeAfterIndicator :before="r.score_before" :after="r.score_after" />
            <span class="opt-date">{{ formatDate(r.created_at) }}</span>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { computed } from "vue"
import TopBar from "../../components/TopBar.vue"
import DashboardNav from "../../components/DashboardNav.vue"
import StatCard from "../../components/StatCard.vue"
import SectionHeader from "../../components/SectionHeader.vue"
import ProgressBar from "../../components/ProgressBar.vue"
import BarChart from "../../components/BarChart.vue"
import BeforeAfterIndicator from "../../components/BeforeAfterIndicator.vue"

const props = defineProps({
  stats:        { type: Object, default: () => ({}) },
  per_project:  { type: Array,  default: () => [] },
  daily_costs:  { type: Array,  default: () => [] },
  improvements: { type: Array,  default: () => [] },
})

const maxCost = computed(() => Math.max(...props.per_project.map((p) => p.cost), 0.0001))

const costTrend = computed(() => {
  if (props.stats.cost_per_req <= 0.40) return "positive"
  if (props.stats.cost_per_req <= 0.60) return "neutral"
  return "negative"
})

const chartData = computed(() =>
  props.daily_costs.map((d) => ({
    value:        d.cost,
    label:        d.date.slice(8),   // just the day number
    displayValue: d.cost > 0 ? "$" + d.cost.toFixed(2) : "",
  }))
)

function scoreColor(score) {
  if (score >= 90) return "#4ade80"
  if (score >= 80) return "#f5a623"
  if (score > 0)   return "#f87171"
  return "rgba(232,230,227,0.25)"
}

function formatDate(dateStr) {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" })
}
</script>

<style scoped>
.page {
  min-height: 100vh;
  background-color: #0a0a0b;
  position: relative;
}

.bg-grid {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image:
    linear-gradient(rgba(245, 166, 35, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(245, 166, 35, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
}

.content {
  position: relative;
  z-index: 1;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.stat-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.main-grid {
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 16px;
  align-items: start;
}

.spend-col,
.right-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.spend-list {
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 14px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.empty-row {
  font-family: var(--font-mono);
  font-size: 12px;
  color: rgba(232, 230, 227, 0.25);
  text-align: center;
  padding: 16px 0;
}

.spend-row {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.spend-info {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.spend-name {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 600;
  color: #e8e6e3;
}

.spend-msgs {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.25);
}

.spend-amount {
  font-family: var(--font-mono);
  font-size: 11px;
  color: #f5a623;
  text-align: right;
}

.chart-panel,
.roi-panel {
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  overflow: hidden;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.panel-label {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: rgba(232, 230, 227, 0.35);
}

.panel-meta {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.25);
}

.chart-body {
  padding: 16px 18px 12px;
}

.roi-table { padding: 0; }

.roi-header {
  display: grid;
  grid-template-columns: 1fr 80px 80px 70px;
  padding: 8px 18px;
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(232, 230, 227, 0.25);
}

.roi-row {
  display: grid;
  grid-template-columns: 1fr 80px 80px 70px;
  align-items: center;
  padding: 10px 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  transition: background 0.15s;
}

.roi-row:hover { background: rgba(245, 166, 35, 0.04); }

.roi-name {
  font-family: var(--font-mono);
  font-size: 12px;
  color: #e8e6e3;
}

.roi-num {
  font-family: var(--font-mono);
  font-size: 11px;
  color: rgba(232, 230, 227, 0.45);
  text-align: right;
}

.roi-score {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  text-align: right;
}

.opt-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.opt-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
}

.opt-card {
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.opt-project {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 600;
  color: rgba(232, 230, 227, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.opt-date {
  font-family: var(--font-mono);
  font-size: 9px;
  color: rgba(232, 230, 227, 0.25);
}
</style>
